import _ from 'lodash';
import { axiosInstance } from '../helpers';
import { deviceType, browserName } from 'react-device-detect';
import { getStoredUser, storeUser } from '../utils/authStorage';

const login = (username, password) => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: { username, password, deviceType, browserName },
  };

  return axiosInstance
    .post(`/users/login`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .then(user => {
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      storeUser(user);

      return user;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const logout = async () => {
  localStorage.removeItem('user');
  return axiosInstance.post('/users/logout', {});
};

const refreshToken = () => {
  return axiosInstance
    .post('/users/refreshToken', {})
    .then(response => {
      if (response.data.success) {
        const user = getStoredUser();
        if (user && response.data.authToken) {
          user.authToken = response.data.authToken;
          storeUser(user);
        }

        return response.data.authToken;
      }
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const getAll = () => {
  return axiosInstance
    .get(`/users`)
    .then(handleResponse)
    .then(data => {
      return data;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const getUser = (userId) => {
  return axiosInstance
    .get(`/users/${userId}`)
    .then(handleResponse)
    .then(data => {
      return data;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const statistics = (query) => {
  return axiosInstance
    .get(`/users/statistics`, { params: query })
    .then(handleResponse)
    .then(data => {
      return data;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const details = (query) => {
  return axiosInstance
    .get(`/users/details`, { params: query })
    .then(handleResponse)
    .then(data => {
      return data;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const newDetails = ({ page = 1, limit = 10, search = '', planStatus = '' }) => {
  return axiosInstance
    .get(`/users/newDetails`, { params: { page, limit, search, planStatus } })
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

const getDesigns = (userId) => {
  return axiosInstance
    .get(`/users/${userId}/designs`)
    .then(handleResponse)
    .then(data => {
      return data;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const getById = id => {
  return axiosInstance.get(`/users/${id}`).then(handleResponse);
};

const register = user => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: { ...user },
  };

  return axiosInstance
    .post(`/users/register`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};


const confirmMobile = props => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: props,
  };

  return axiosInstance
    .post(`/users/confirmMobile`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

const resendConfirmationSMS = props => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: props,
  };

  return axiosInstance
    .post(`/users/confirmMobile`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

const update = user => {
  const { userId, ...payload } = user;
  // delete user.verified
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: payload,
  };

  return axiosInstance
    .put(`/users/${userId}`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

// prefixed const name with underscore because delete is a reserved word in javascript
const _delete = id => {
  return axiosInstance.delete(`/users/${id}`).then(handleResponse);
};

const authenticate = password => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: { password },
  };

  return axiosInstance
    .post(`/users?authenticate`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

const findAllFolders = userId => {
  return axiosInstance
    .get(`/users/${userId}/folders`)
    .then(handleResponse)
    .then(data => {
      return data.folders;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const createFolder = (userId, name) => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: { name },
  };

  return axiosInstance
    .post(`/users/${userId}/folders`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .then(data => {
      return data.folders;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const deleteFolder = (userId, folderId) => {
  return axiosInstance
    .delete(`/users/${userId}/folders/${folderId}`)
    .then(handleResponse)
    .then(data => {
      return data.folders;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const forgotPassword = props => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: props,
  };

  return axiosInstance
    .post(`/users/forgotPassword`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

const resetPassword = props => {
  const requestOptions = {
    headers: { 'Content-Type': 'application/json' },
    body: props,
  };

  return axiosInstance
    .post(`/users/forgotPassword/resetPassword`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

const changeAvatar = (userId, files) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('file', file);
  });

  const requestOptions = {
    headers: { 'Content-Type': 'multipart/form-data' },
    body: formData,
  };

  return axiosInstance
    .post(`/users/${userId}/avatar`, requestOptions.body, requestOptions.headers)
    .then(handleResponse)
    .then(response => {
      return response;
    })
    .catch(error => {
      handleResponse(error.response);
    });
};

const getDashboardStats = () => {
  return axiosInstance
    .get(`/dashboard/stats`)
    .then(handleResponse)
    .catch(error => {
      handleResponse(error.response);
    });
};

const handleResponse = response => {
  if (!response) {
    const unavailableError = new Error('Unable to reach the server');
    unavailableError.status = 500;
    unavailableError.error = unavailableError.message;
    throw unavailableError;
  }

  const data = response.data;
  if (!_.includes([200, 201, 204], response.status)) {
    if (response.status === 401 && !response.config.url.includes('/users/login')) {
      // auto logout if 401 response returned from api
      logout();
      window.location.reload(true);
    }

    const message = (data && data.message) || response.statusText || 'Request failed';
    const responseError = new Error(message);
    responseError.status = response.status;
    responseError.error = message;
    responseError.code = data && data.code;
    responseError.name = (data && data.name) || responseError.name;
    throw responseError;
  }

  return data;
};

export const userService = {
  login,
  logout,
  register,
  refreshToken,
  getAll,
  getUser,
  statistics,
  details,
  newDetails,
  getById,
  update,
  delete: _delete,
  authenticate,
  findAllFolders,
  createFolder,
  confirmMobile,
  resendConfirmationSMS,
  forgotPassword,
  resetPassword,
  changeAvatar,
  deleteFolder,
  getDesigns,
  getDashboardStats,
};
